// v2 wolf — a rangy hunter: tall legs under a long level-backed body with a deep
// narrow chest, a dark saddle over grizzled flanks, a thick neck ruff carrying
// the head slightly forward, a long muzzle with pricked ears, a pale throat and a
// straight bushy tail held out behind. Two coats: grey timber wolf and
// black-phase. Ported 1:1 from age-of-mcp's v2/wolf.ts, using the library's
// shared `critter` (quadLegs) helper.
import * as THREE from "three";
import { defineModel, critter, type Part } from "../../lib";

const COATS = [
  { body: "#84888e", chest: "#8e9298", saddle: "#565a60", pale: "#d9d5cb", leg: "#6a6e74", ear: "#4e5258" },
  { body: "#3c3e44", chest: "#44464c", saddle: "#25272b", pale: "#8e8e8a", leg: "#2e3034", ear: "#202226" },
];

function wolfParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.06, dz: 0.024, bodyY: 0.085, rTop: 0.007, rFoot: 0.0052, color: c.leg,
  });
  parts.push(
    // long lean torso with a deep chest
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: c.body,
      position: [0, 0.085, 0], scale: [1.8, 0.9, 0.78] },
    { geometry: new THREE.IcosahedronGeometry(0.033, 0), color: c.chest,
      position: [0.048, 0.082, 0], scale: [1.0, 1.05, 0.82] },
    // dark saddle along the back
    { geometry: new THREE.IcosahedronGeometry(0.031, 0), color: c.saddle,
      position: [-0.012, 0.102, 0], scale: [1.5, 0.55, 0.8] },
    // pale throat under a thick neck ruff
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.pale,
      position: [0.065, 0.066, 0], scale: [0.9, 1.0, 0.8] },
    { geometry: new THREE.IcosahedronGeometry(0.0265, 0), color: c.body,
      position: [0.072, 0.099, 0], scale: [0.95, 0.95, 1.05] },
    // head carried forward, long muzzle, black nose
    { geometry: new THREE.IcosahedronGeometry(0.0225, 0), color: c.chest,
      position: [0.1, 0.111, 0], scale: [1.2, 0.95, 0.9] },
    { geometry: new THREE.ConeGeometry(0.0105, 0.034, 4), color: c.saddle,
      position: [0.128, 0.104, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.004, 0), color: "#14110e",
      position: [0.145, 0.104, 0] },
    // pricked ears
    { geometry: new THREE.ConeGeometry(0.0085, 0.021, 4), color: c.ear,
      position: [0.091, 0.133, 0.014], rotation: [0.18, 0, 0] },
    { geometry: new THREE.ConeGeometry(0.0085, 0.021, 4), color: c.ear,
      position: [0.091, 0.133, -0.014], rotation: [-0.18, 0, 0] },
    // straight bushy tail held out behind, dark tip
    { geometry: new THREE.CylinderGeometry(0.0065, 0.0125, 0.055, 4), color: c.body,
      position: [-0.092, 0.073, 0], rotation: [0, 0, 1.22] },
    { geometry: new THREE.IcosahedronGeometry(0.0095, 0), color: c.saddle,
      position: [-0.12, 0.083, 0] },
  );
  return parts;
}

export const wolfV2 = defineModel({
  id: "wolf-v2",
  batchId: "fable",
  name: "Wolf (v2)",
  tags: ["creature","wolf","low-poly","age-of-mcp","fable"],
  summary: "Low-poly wolf.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.13,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => wolfParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => wolfParts(1) },
  ],
  animations: [],
});
